import { markRaw } from "vue";
import SearchComponent from "../../components/misc/AppSearch/SearchComponent";
import { Patient } from "../../components/misc/AppSearch/SearchComponent/helpers";
import { showDialog } from "../../utils/dialog";
import { dispatch } from "../../utils/bus";
import { openModal } from "./drawer";

export interface PatientSearchOptions {
  title?: string;
  success?: (patient: Patient) => PromiseLike<void>;
  cancel?: () => PromiseLike<void>;
  modalContentTitle?: string;
  excludedPatientIds?: string[];
  bannerType?: string;
  bannerMessage?: string;
  showRecentPatients?: boolean;
  overriddenEmptyText?: string;
  showAdvancedOptions?: boolean;
}

export function patientSearch(opts?: PatientSearchOptions): Promise<string | null> {
  return new Promise((res) => {
    let resolved = false;
    showDialog({
      title: opts?.title ?? "Find a Patient",
      noButtons: true,
      component: markRaw(SearchComponent),
      type: "small",
      class: "app-search-modal",

      contentClass: "h-full",

      transient: true,

      componentProps: {
        onSelect(patient: Patient) {
          resolved = true;
          opts?.success?.(patient);
          res(patient.value);

          // ENG-21464: re-run the underlying modal's focus trap now that this dialog has focus.
          dispatch("reloadData", true);
        },
        modalContentTitle: opts?.modalContentTitle,
        excludedPatientIds: opts?.excludedPatientIds,
        bannerType: opts?.bannerType,
        bannerMessage: opts?.bannerMessage,
        showRecentPatients: opts?.showRecentPatients,
        overriddenEmptyText: opts?.overriddenEmptyText,
        showAdvancedOptions: opts?.showAdvancedOptions,
      },
    }).onFinally(() => {
      if (resolved) return;
      opts?.cancel?.();
      res(null);
    });
  });
}

// ENG-21550: same picker as patientSearch(), but as a draggable Modal
// instead of a Dialog, via openModal's URL mode instead of showDialog.
// TODO(ENG-21575): decide whether every patient-search opener should use
// openModal instead of showDialog, and collapse these two into one.
export function patientSearchModal(opts?: PatientSearchOptions): Promise<string | null> {
  return new Promise((res) => {
    let resolved = false;

    openModal("/staff/recent-patients", {
      ariaLabel: opts?.modalContentTitle ?? "Find a Patient",
      ariaLabelledBy: false,
      props: {
        onSelect(patient: Patient) {
          resolved = true;
          opts?.success?.(patient);
          res(patient.value);

          // ENG-21464: re-run the underlying modal's focus trap now that the picker has focus.
          dispatch("reloadData", true);
        },
        // Guard against a late unmount-cancel resolving the promise after a select.
        onCancel: () => {
          if (resolved) return;
          opts?.cancel?.();
          res(null);
        },
        modalContentTitle: opts?.modalContentTitle,
        excludedPatientIds: opts?.excludedPatientIds,
        bannerType: opts?.bannerType,
        bannerMessage: opts?.bannerMessage,
        showRecentPatients: opts?.showRecentPatients,
        overriddenEmptyText: opts?.overriddenEmptyText,
        showAdvancedOptions: opts?.showAdvancedOptions,
      },
    });
  });
}
